import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import polyline from "google-polyline";
import { Button, Card, Map } from "@cofano/cck";
import { Box, Divider } from "@mui/material";
import { Layers, LocationOn, PolylineRounded } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";
import MapOverlayControls from "view/locations/components/map/MapOverlayControls";
import { cleanlyDecodePolyline, LocationTabs, MAP_HEIGHT } from "view/locations/utils/LocationUtils";
import { GeofenceMapApi, getGeofenceLabels, MapRef, MapsRef, Point, useGeofenceShapes, useOtherGeofences } from "view/locations/utils/GeofenceUtils";
import { getMapCenterPosition } from "view/maps/utils/MapUtils";
import { LocationSchemaInput, LocationSchemaOutput } from "view/locations/utils/location.schema";

enum Mode {
	Marker = "marker",
	Geofence = "geofence"
}

interface GeofenceShape {
	color: string;
	fillColor: string;
	fillOpacity: number;
	line: Point[];
}

const GEOFENCE_STYLE = { color: "red", fillColor: "#ff848482", fillOpacity: 1 };

type Path = google.maps.MVCArray<google.maps.LatLng>;

const pathToPoints = (path: Path): Point[] => path.getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));

const isSamePath = (path: Path, points: Point[]) => path.getLength() === points.length
	&& points.every((point, index) => path.getAt(index).lat() === point.lat && path.getAt(index).lng() === point.lng);

const LocationMapForm = () => {

	const { setValue, getValues } = useFormContext<LocationSchemaInput, unknown, LocationSchemaOutput>();
	const session = useStoreState(store => store.session);
	const navigate = useNavigate();

	const originalPosition = getValues("position");

	const initialPosition = getMapCenterPosition(session, {
		latitude: originalPosition.latitude,
		longitude: originalPosition.longitude,
	});

	const initialPolylines = cleanlyDecodePolyline(getValues("encodedPolyline") || "");

	const [ marker, setMarker ] = useState(initialPosition);
	const [ mode, setMode ] = useState(Mode.Marker);
	const [ showOtherGeofences, setShowOtherGeofences ] = useState(true);
	const [ mapApi, setMapApi ] = useState<GeofenceMapApi>();
	const [ polylines, setPolylines ] = useState<GeofenceShape[]>([ { ...GEOFENCE_STYLE, line: initialPolylines[0].line } ]);

	const polylinesRef = useRef(polylines);
	const modeRef = useRef(mode);
	const polygonRef = useRef<google.maps.Polygon>();
	const mapRef = useRef<MapRef>();
	const mapsRef = useRef<MapsRef>();
	const syncingRef = useRef(false);

	const otherGeofences = useOtherGeofences(getValues("id"));

	const shownGeofences = useMemo(
		() => showOtherGeofences ? otherGeofences : [],
		[ otherGeofences, showOtherGeofences ],
	);

	const otherGeofenceLabels = useMemo(() => getGeofenceLabels(shownGeofences), [ shownGeofences ]);

	// when clicking on a geofence, navigate to that location
	useGeofenceShapes(mapApi, shownGeofences, mode === Mode.Marker, geofence => {
		navigate(`/view/locations/${geofence.id}/edit`, { state: { tab: LocationTabs.MAP } });
	});

	useEffect(() => {
		setValue("position", { latitude: marker.lat, longitude: marker.lng });
	}, [ marker ]);

	useEffect(() => {
		modeRef.current = mode;
		polygonRef.current?.setOptions({ editable: mode === Mode.Geofence });
	}, [ mode ]);

	useEffect(() => {
		polylinesRef.current = polylines;
		setValue("encodedPolyline", polyline.encode(polylines[0].line.map(l => [ l.lat, l.lng ])) as string);

		const polygon = polygonRef.current;
		const maps = mapsRef.current;
		if (!polygon || !maps || isSamePath(polygon.getPath(), polylines[0].line)) {
			return;
		}

		const path = polygon.getPath();
		syncingRef.current = true;
		while (path.getLength() > polylines[0].line.length) {
			path.removeAt(path.getLength() - 1);
		}

		polylines[0].line.forEach((point, index) => {
			if (index < path.getLength()) {
				path.setAt(index, new maps.LatLng(point.lat, point.lng));
			} else {
				path.push(new maps.LatLng(point.lat, point.lng));
			}
		});
		syncingRef.current = false;
	}, [ polylines ]);

	useEffect(() => () => {
		const polygon = polygonRef.current;
		const maps = mapsRef.current;
		if (polygon && maps) {
			maps.event.clearInstanceListeners(polygon.getPath());
			maps.event.clearInstanceListeners(polygon);
			polygon.setMap(null);
			polygonRef.current = undefined;
		}
	}, []);

	const onMapClick = (lat: number, lng: number) => {
		if (modeRef.current == Mode.Marker) {
			setMarker({ lat: lat, lng: lng });
		} else {
			setPolylines([ { ...GEOFENCE_STYLE, line: [ ...polylinesRef.current[0].line, { lat: lat, lng: lng } ] } ]);
		}
	};

	const syncPath = () => {
		const polygon = polygonRef.current;
		if (polygon && !syncingRef.current) {
			setPolylines([ { ...GEOFENCE_STYLE, line: pathToPoints(polygon.getPath()) } ]);
		}
	};

	const onGoogleApiLoaded = (map: MapRef, maps: MapsRef) => {
		mapRef.current = map;
		mapsRef.current = maps;

		const polygon = new maps.Polygon({
			paths: [ polylinesRef.current[0].line ],
			strokeColor: GEOFENCE_STYLE.color,
			strokeOpacity: 1,
			fillColor: GEOFENCE_STYLE.fillColor,
			fillOpacity: GEOFENCE_STYLE.fillOpacity,
			editable: modeRef.current === Mode.Geofence,
			zIndex: 2,
			map: map,
		});
		polygonRef.current = polygon;

		const path = polygon.getPath();

		// sync with the internal google maps polygon
		maps.event.addListener(path, "set_at", syncPath);
		maps.event.addListener(path, "insert_at", syncPath);
		maps.event.addListener(path, "remove_at", syncPath);

		// right clicking removes the point
		maps.event.addListener(polygon, "rightclick", (event: google.maps.PolyMouseEvent) => {
			if (event.vertex !== undefined) {
				path.removeAt(event.vertex);
			}
		});

		// clicks through the polygon go to the map
		maps.event.addListener(polygon, "click", (event: google.maps.PolyMouseEvent) => {
			if (event.vertex === undefined && event.edge === undefined && event.latLng) {
				onMapClick(event.latLng.lat(), event.latLng.lng());
			}
		});

		setMapApi({ map: map, maps: maps });
	};

	return (
		<Card disablePadding>
			<Box sx={{ height: MAP_HEIGHT, position: "relative", width: "100%" }}>
				<MapOverlayControls>
					<Button
						icon={<LocationOn />}
						variant={mode === Mode.Marker ? "contained" : "text"}
						onClick={() => setMode(Mode.Marker)}  />

					<Button
						icon={<PolylineRounded />}
						variant={mode === Mode.Geofence ? "contained" : "text"}
						onClick={() => setMode(Mode.Geofence)} />

					<Divider sx={{ my: 0.5 }} />

					<Button
						icon={<Layers />}
						variant={showOtherGeofences ? "contained" : "text"}
						onClick={() => setShowOtherGeofences(!showOtherGeofences)} />

					{mode === Mode.Geofence && (
						<>
							<Divider sx={{ my: 0.5 }} />

							<Button
								icon={<Delete fontSize="small" />}
								onClick={() => setPolylines([ { ...polylines[0], line: [] } ])}
								variant="text" />
						</>
					)}
				</MapOverlayControls>

				<Map
					apiKey={"AIzaSyAFEhlhSJ7c85rDGz1JX99veD0sshXx_xs"}
					center={initialPosition}
					disableBounds
					markers={[ marker, ...otherGeofenceLabels ]}
					onClick={onMapClick}
					onGoogleApiLoaded={onGoogleApiLoaded}
					zoom={15} />
			</Box>
		</Card>
	);
};

export default LocationMapForm;
